import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { getWorkflowNodes } from "../../services/workflowGeneration";
import { extractCategories, groupNodesByCategory, normalizeWorkflowNodes } from "../../utils/workflowNodes";
import NodeIcon from "./NodeIcon";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  color: var(--main-font-color);
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background-color: var(--main-bg);

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: var(--main-font-color);
    font-size: 13px;
    font-family: inherit;
  }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 2px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0a6b1;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color ?? "var(--button-bg)"};
  flex-shrink: 0;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    background-color: var(--content-item-bg);
    border-color: var(--content-item-border-color);
  }
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25;
`;

const ItemDescription = styled.div`
  font-size: 11px;
  color: #a0a6b1;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const Muted = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #a0a6b1;
  padding: 4px 2px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  padding: 8px;
  border: 1px solid #c0392b;
  border-radius: 6px;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

/**
 * The node palette for the workflow builder, driven by GET /admin/workflow/nodes.
 *
 * Groups nodes by the server's categories (with their colours), and offers a client-side
 * search over label / type / role / description. Clicking a node hands its full definition to
 * `onAdd`.
 */
export const NodePalette = ({ onAdd = () => { } }) => {
  const [nodes, setNodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;

    getWorkflowNodes()
      .then((result) => {
        if (cancelled) return;
        if (!result.success) {
          setError(result.message || "Could not load the node palette.");
          return;
        }
        setNodes(normalizeWorkflowNodes(result.payload));
        setCategories(extractCategories(result.payload));
        setError(null);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError?.message || "Could not load the node palette.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? nodes.filter((node) =>
          [node.name, node.type, node.role, node.description, node.category]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        )
      : nodes;

    return groupNodesByCategory(filtered, categories);
  }, [nodes, categories, query]);

  return (
    <Wrapper>
      <SearchBox>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a0a6b1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          value={query}
          placeholder="Search nodes"
          onChange={(event) => setQuery(event.target.value)}
        />
      </SearchBox>

      {loading ? (
        <Muted>Loading nodes…</Muted>
      ) : error ? (
        <ErrorText>{error}</ErrorText>
      ) : groups.length === 0 ? (
        <Muted>No matching nodes.</Muted>
      ) : (
        groups.map((group) => (
          <Group key={group.category.id}>
            <GroupHeader>
              <Dot color={group.category.color} />
              {group.category.label}
              <span style={{ fontWeight: 400 }}>{group.nodes.length}</span>
            </GroupHeader>
            {group.nodes.map((node) => (
              <Item key={node.type} type="button" title={node.description ?? node.name} onClick={() => onAdd(node)}>
                <NodeIcon icon={node.icon} size={16} color={node.categoryColor ?? group.category.color ?? "currentColor"} />
                <ItemText>
                  <ItemTitle>{node.name}</ItemTitle>
                  {node.description ? <ItemDescription>{node.description}</ItemDescription> : null}
                </ItemText>
              </Item>
            ))}
          </Group>
        ))
      )}
    </Wrapper>
  );
};

export default NodePalette;
